import { useMemo, useState } from "react";
import type { ChannelDelayThresholdRow, ChannelDelayThresholdsMap } from "../types/preferences_types";

const DEFAULT_THRESHOLD_SECONDS = 60 * 60 * 3;

export function validateHoursMinutes(hours: string, minutes: string): { ok: true } | { ok: false; error: string } {
    const h = Number(hours);
    const m = Number(minutes);
    if (!Number.isInteger(h) || !Number.isInteger(m)) return { ok: false, error: "올바른 정수값을 입력해주세요." };
    if (h < 0) return { ok: false, error: "시간은 0 이상의 값을 입력해야 합니다." };
    if (m < 0 || m > 59) return { ok: false, error: "분은 0에서 59 사이의 값을 입력해야 합니다." };
    return { ok: true };
}

function keyOf(channel: string, subChannel?: string) {
    return subChannel ? `${channel}:${subChannel}` : channel;
}

export function useChannelDelayThresholds() {
    // TODO: 실제 API 연결 시 /versions + parameter-store 값을 fetch해서 rows 구성
    const initialRows: ChannelDelayThresholdRow[] = useMemo(
        () => [
            { channel: "facebook", subChannel: "insights", hours: "3", minutes: "0" },
            { channel: "facebook", subChannel: "resources", hours: "2", minutes: "30" },
            { channel: "google", subChannel: undefined, hours: "3", minutes: "0" },
            { channel: "tiktok", subChannel: undefined, hours: "4", minutes: "0" },
        ],
        []
    );

    const [rows, setRows] = useState<ChannelDelayThresholdRow[]>(initialRows);
    const [isLoading, setIsLoading] = useState(false);

    const load = async () => {
        setIsLoading(true);
        try {
            // TODO: 실제 데이터 로드
            await new Promise((r) => setTimeout(r, 300));
        } finally {
            setIsLoading(false);
        }
    };

    const updateRow = (index: number, patch: Partial<Pick<ChannelDelayThresholdRow, "hours" | "minutes">>) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const save = async () => {
        setIsLoading(true);
        try {
            // validate all
            for (const r of rows) {
                const v = validateHoursMinutes(String(r.hours), String(r.minutes));
                if (!v.ok) return { ok: false as const, error: v.error };
            }

            const thresholds: ChannelDelayThresholdsMap = {};
            for (const r of rows) {
                const totalSeconds = Number(r.hours) * 3600 + Number(r.minutes) * 60;
                thresholds[keyOf(r.channel, r.subChannel)] = totalSeconds || DEFAULT_THRESHOLD_SECONDS;
            }

            // TODO: parameter store 저장 API
            await new Promise((r) => setTimeout(r, 500));

            return { ok: true as const, thresholds };
        } finally {
            setIsLoading(false);
        }
    };

    return {
        rows,
        isLoading,
        load,
        updateRow,
        save,
    };
}